<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :open-on-click="false"
    :open-on-focus="false"
    transition="scale-transition"
    offset-y
    min-width="auto"
    :disabled="!showPicker"
  >
    <template v-slot:activator="{ attrs }">
      <v-text-field
        ref="input"
        v-model="value"
        v-bind="attrs"
        :label="translatedLabel"
        :name="name || label"
        :hint="hintText"
        :placeholder="placeholderText"
        :append-icon="appendIcon"
        inputmode="numeric"
        maxlength="16"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        @input.native="onNativeInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.alt.down.prevent="openPicker"
        @keydown.esc="menu = false"
        @click:append="openPicker"
        @click:clear="onClear"
        autocomplete="ansdoasdjosk"
        :rules="validationRules"
        :error-messages="errorMessages"
        :clearable="clearable"
        :success="showSuccess"
        :error="showError"
        :color="isFocused ? 'primary' : undefined"
        :aria-label="translatedLabel"
        :aria-invalid="showError ? 'true' : 'false'"
        :aria-required="obrigatorio ? 'true' : 'false'"
        validate-on-blur
        hide-details="auto"
        class="
          input-style
          font-size-input
          text-light-input
          placeholder-light
          input-icon
        "
        dense
        flat
        filled
        solo
      >
      </v-text-field>
    </template>
    <v-card
      @mousedown.capture="onMenuPointerDown"
      @pointerdown.capture="onMenuPointerDown"
    >
      <v-date-picker
        v-model="pickerDate"
        :locale="pickerLocale"
        @input="onPickerDate"
      />
      <v-time-picker v-model="pickerTime" format="24hr" @input="onPickerTime" />
    </v-card>
  </v-menu>
</template>

<script>
import { translateIfMessageExists } from "@/utils/i18n";

export default {
  name: "CampoDataHora",

  props: {
    label: { type: String, default: "Data" },
    name: { type: String, required: false },
    payload: { type: String, required: false },
    formato: { type: String, default: "AUTO" },
    clearable: { type: Boolean, default: true },
    obrigatorio: { type: Boolean, default: true },
    showPicker: { type: Boolean, default: true },
    anoMinimo: { type: Number, default: 1900 },
    anoMaximo: { type: Number, default: 2100 },
  },

  mounted() {
    setTimeout(() => {
      this.setFromPayload(this.payload);
    }, 700);
  },

  watch: {
    payload(val) {
      this.setFromPayload(val);
    },
    menu(val) {
      if (val) {
        this.syncPickersFromValue();
      }
    },
  },

  data() {
    return {
      value: "",
      menu: false,
      pickerDate: null,
      pickerTime: null,
      isFocused: false,
      ignoreBlur: false,
    };
  },

  methods: {
    onFocus() {
      this.isFocused = true;
    },

    onBlur() {
      this.isFocused = false;
      if (this.ignoreBlur) {
        this.ignoreBlur = false;
        return;
      }
      const applied = this.applyShortcut();
      if (!applied && /[a-zA-Z]/.test(this.value || "")) {
        this.value = "";
      }
      this.menu = false;
      this.$emit("date", this.formatedValue);
    },

    openPicker() {
      if (this.showPicker) {
        this.menu = true;
      }
    },

    onMenuPointerDown() {
      this.ignoreBlur = true;
    },

    onPickerDate(value) {
      if (!value) {
        return;
      }
      this.pickerDate = value;
      this.applyPickerValues(false);
    },

    onPickerTime(value) {
      if (!value) {
        return;
      }
      this.pickerTime = value;
      this.applyPickerValues(true);
    },

    applyPickerValues(closeMenu) {
      if (!this.pickerDate && !this.pickerTime) {
        return;
      }
      let dateParts =
        this.parseIsoDate(this.pickerDate) ||
        this.parseIsoDate(this.formatedValue);
      const timeParts =
        this.parseTime(this.pickerTime) || this.getTimePartsFromDisplay();
      if (!dateParts) {
        const now = new Date();
        dateParts = {
          day: this.padZero(now.getDate()),
          month: this.padZero(now.getMonth() + 1),
          year: String(now.getFullYear()),
        };
      }
      const display = this.formatDisplayFromParts({
        day: dateParts.day,
        month: dateParts.month,
        year: dateParts.year,
        hour: timeParts.hour,
        minute: timeParts.minute,
      });
      this.value = display;
      if (closeMenu) {
        this.menu = false;
      }
      this.$emit("date", this.formatedValue);
    },

    onClear() {
      this.value = "";
      this.pickerDate = null;
      this.pickerTime = null;
      this.menu = false;
      this.$emit("date", null);
    },

    setFromPayload(payload) {
      if (!payload) {
        this.value = "";
        return;
      }
      const isoParts = this.parseIsoDateTime(payload);
      if (isoParts) {
        this.value = this.formatDisplayFromParts(isoParts);
        return;
      }
      if (payload.includes("/")) {
        if (this.isMdy && payload.length >= 10) {
          const parts = this.parseDisplayParts(payload, false);
          if (parts) {
            this.value = this.formatDisplayFromParts({
              day: parts.day,
              month: parts.month,
              year: parts.year,
              hour: parts.hour,
              minute: parts.minute,
            });
            return;
          }
        }
        this.value = payload;
        return;
      }
      if (this.$options.filters && this.$options.filters.dataHoraParaLeitura) {
        this.value = this.$options.filters.dataHoraParaLeitura(payload);
      } else {
        this.value = payload;
      }
    },

    syncPickersFromValue() {
      const iso = this.displayToIso(this.value);
      if (iso) {
        this.pickerDate = iso.substring(0, 10);
        this.pickerTime = iso.length >= 16 ? iso.substring(11, 16) : null;
      } else {
        this.pickerDate = null;
        this.pickerTime = null;
      }
    },

    onNativeInput(event) {
      const raw = (event && event.target && event.target.value) || "";
      const selectionStart =
        event && event.target ? event.target.selectionStart : null;
      const isoParts = this.parseIsoDateTime(raw);
      if (isoParts) {
        const normalized = this.normalizeIsoParts(isoParts);
        const formatted = this.formatDisplayFromParts(normalized);
        this.value = formatted;
        this.setCaret(formatted.length);
        return;
      }
      const trimmed = String(raw || "").trim();
      if (/[a-zA-Z]/.test(trimmed) || /^[+-]/.test(trimmed)) {
        this.value = raw;
        return;
      }
      const { formatted, caret } = this.applyMask(raw, selectionStart);
      this.value = formatted;
      if (caret !== null) {
        this.setCaret(caret);
      }
    },

    applyMask(raw, selectionStart) {
      const digits = this.onlyDigits(raw).slice(0, 12);
      const sanitized = this.sanitizeDateTimeDigits(digits);
      const formatted = this.formatWithPattern(sanitized, "##/##/#### ##:##");
      if (typeof selectionStart !== "number") {
        return { formatted, caret: null };
      }
      const digitsBeforeCaret = this.onlyDigits(
        raw.slice(0, selectionStart)
      ).length;
      const preferSeparator =
        selectionStart > 0 &&
        raw[selectionStart - 1] &&
        !/\d/.test(raw[selectionStart - 1]);
      const caret = this.caretFromDigits(
        formatted,
        digitsBeforeCaret,
        preferSeparator
      );
      return { formatted, caret };
    },

    sanitizeDateTimeDigits(digits) {
      if (!digits) {
        return "";
      }
      const dateDigits = digits.slice(0, 8);
      const timeDigits = digits.slice(8, 12);
      const safeDate = this.sanitizeDateDigits(dateDigits);
      const safeTime = this.sanitizeTimeDigits(timeDigits);
      return safeDate + safeTime;
    },

    sanitizeDateDigits(digits) {
      if (!digits) {
        return "";
      }
      const first = digits.slice(0, 2);
      const second = digits.slice(2, 4);
      const year = digits.slice(4, 8);

      let day = this.isMdy ? second : first;
      let month = this.isMdy ? first : second;

      month = this.clampSegment(month, 12);
      day = this.clampSegment(day, 31);

      if (day.length === 2 && month.length === 2 && year.length === 4) {
        const yearNum = Number(year);
        const monthNum = Number(month);
        if (!Number.isNaN(yearNum) && monthNum >= 1 && monthNum <= 12) {
          const maxDay = new Date(yearNum, monthNum, 0).getDate();
          const dayNum = Number(day);
          if (!Number.isNaN(dayNum)) {
            const adjusted = Math.min(Math.max(dayNum, 1), maxDay);
            day = String(adjusted).padStart(2, "0");
          }
        }
      }

      return this.isMdy ? `${month}${day}${year}` : `${day}${month}${year}`;
    },

    sanitizeTimeDigits(digits) {
      if (!digits) {
        return "";
      }
      const hour = digits.slice(0, 2);
      const minute = digits.slice(2, 4);
      const safeHour = this.clampSegment(hour, 23, 0);
      const safeMinute = this.clampSegment(minute, 59, 0);
      return `${safeHour}${safeMinute}`;
    },

    clampSegment(segment, max, min = 1) {
      if (!segment || segment.length < 2) {
        return segment || "";
      }
      let num = Number(segment);
      if (Number.isNaN(num)) {
        return segment;
      }
      if (num < min) {
        num = min;
      }
      if (num > max) {
        num = max;
      }
      return String(num).padStart(2, "0");
    },

    normalizeIsoParts(parts) {
      const yearNum = Number(parts.year);
      let monthNum = Number(parts.month);
      let dayNum = Number(parts.day);
      let hourNum = Number(parts.hour);
      let minuteNum = Number(parts.minute);

      if (Number.isNaN(monthNum) || monthNum <= 0) {
        monthNum = 1;
      }
      if (monthNum > 12) {
        monthNum = 12;
      }

      if (Number.isNaN(dayNum) || dayNum <= 0) {
        dayNum = 1;
      }
      if (!Number.isNaN(yearNum) && monthNum >= 1 && monthNum <= 12) {
        const maxDay = new Date(yearNum, monthNum, 0).getDate();
        if (dayNum > maxDay) {
          dayNum = maxDay;
        }
      } else if (dayNum > 31) {
        dayNum = 31;
      }

      if (Number.isNaN(hourNum) || hourNum < 0) {
        hourNum = 0;
      }
      if (hourNum > 23) {
        hourNum = 23;
      }

      if (Number.isNaN(minuteNum) || minuteNum < 0) {
        minuteNum = 0;
      }
      if (minuteNum > 59) {
        minuteNum = 59;
      }

      return {
        year: String(Number.isNaN(yearNum) ? parts.year : yearNum),
        month: this.padZero(monthNum),
        day: this.padZero(dayNum),
        hour: this.padZero(hourNum),
        minute: this.padZero(minuteNum),
      };
    },

    normalizeKeyword(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    applyShortcut() {
      const rawValue = this.value || "";
      if (!rawValue) {
        return false;
      }
      const normalized = this.normalizeKeyword(rawValue);
      if (!normalized) {
        return false;
      }
      let dayOffset = null;
      let useNow = false;
      if (normalized === "agora") {
        dayOffset = 0;
        useNow = true;
      } else if (normalized === "hoje") {
        dayOffset = 0;
      } else if (normalized === "amanha") {
        dayOffset = 1;
      } else if (normalized === "ontem") {
        dayOffset = -1;
      } else {
        const match = normalized.match(/^([+-]?\d+)\s*d$/);
        if (match) {
          dayOffset = Number(match[1]);
        }
      }
      if (dayOffset === null || Number.isNaN(dayOffset)) {
        return false;
      }
      const base = new Date();
      if (!useNow) {
        base.setHours(0, 0, 0, 0);
      }
      base.setDate(base.getDate() + dayOffset);
      const timeParts = useNow
        ? {
            hour: this.padZero(base.getHours()),
            minute: this.padZero(base.getMinutes()),
          }
        : this.getTimePartsFromDisplay();
      this.value = this.formatDisplayFromParts({
        day: this.padZero(base.getDate()),
        month: this.padZero(base.getMonth() + 1),
        year: String(base.getFullYear()),
        hour: timeParts.hour,
        minute: timeParts.minute,
      });
      return true;
    },

    parseIsoDate(raw) {
      const match = String(raw || "").match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!match) {
        return null;
      }
      return { year: match[1], month: match[2], day: match[3] };
    },

    parseIsoDateTime(raw) {
      const match = String(raw || "").match(
        /(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/
      );
      if (!match) {
        return null;
      }
      const hour = match[4] || "00";
      const minute = match[5] || "00";
      return {
        year: match[1],
        month: match[2],
        day: match[3],
        hour,
        minute,
      };
    },

    parseDisplayParts(value, allowMdy = true) {
      const [datePart, timePart] = String(value || "").split(" ");
      if (!datePart || !timePart) {
        return null;
      }
      const dateSegments = datePart.split("/");
      if (dateSegments.length < 3) {
        return null;
      }
      const timeSegments = timePart.split(":");
      const hour = timeSegments[0] || "00";
      const minute = timeSegments[1] || "00";
      const first = dateSegments[0];
      const second = dateSegments[1];
      const third = dateSegments[2];
      if (this.isMdy && allowMdy) {
        return { day: second, month: first, year: third, hour, minute };
      }
      return { day: first, month: second, year: third, hour, minute };
    },

    parseTime(value) {
      if (!value) {
        return null;
      }
      const [hour, minute] = String(value).split(":");
      return {
        hour: hour || "00",
        minute: minute || "00",
      };
    },

    getTimePartsFromDisplay() {
      if (!this.value || this.value.length < 16) {
        return { hour: "00", minute: "00" };
      }
      const timePart = this.value.split(" ")[1] || "";
      const timeSegments = timePart.split(":");
      return {
        hour: timeSegments[0] || "00",
        minute: timeSegments[1] || "00",
      };
    },

    formatDisplayFromParts({ day, month, year, hour, minute }) {
      const safeDay = this.padZero(day);
      const safeMonth = this.padZero(month);
      const safeHour = this.padZero(hour);
      const safeMinute = this.padZero(minute);
      const datePart = this.isMdy
        ? `${safeMonth}/${safeDay}/${year}`
        : `${safeDay}/${safeMonth}/${year}`;
      return `${datePart} ${safeHour}:${safeMinute}`;
    },

    displayToIso(value) {
      if (!value || value.length < 16) {
        return null;
      }
      const parts = this.parseDisplayParts(value);
      if (!parts) {
        return null;
      }
      const month = this.padZero(parts.month);
      const day = this.padZero(parts.day);
      const hour = this.padZero(parts.hour);
      const minute = this.padZero(parts.minute);
      return `${parts.year}-${month}-${day}T${hour}:${minute}:00`;
    },

    isValidDateTime(value) {
      if (!value || value.length < 16) {
        return false;
      }
      const parts = this.parseDisplayParts(value);
      if (!parts) {
        return false;
      }
      const year = Number(parts.year);
      const month = Number(parts.month);
      const day = Number(parts.day);
      const hour = Number(parts.hour);
      const minute = Number(parts.minute);
      if (!year || !month || !day) {
        return false;
      }
      if (this.anoMinimo && year < this.anoMinimo) {
        return false;
      }
      if (this.anoMaximo && year > this.anoMaximo) {
        return false;
      }
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return false;
      }
      const dateObj = new Date(year, month - 1, day);
      return (
        dateObj.getFullYear() === year &&
        dateObj.getMonth() === month - 1 &&
        dateObj.getDate() === day
      );
    },

    padZero(value) {
      return String(value).padStart(2, "0");
    },

    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    formatWithPattern(digits, pattern) {
      if (!digits) {
        return "";
      }
      let result = "";
      let digitIndex = 0;
      for (let i = 0; i < pattern.length; i++) {
        const char = pattern[i];
        if (char === "#") {
          if (digitIndex < digits.length) {
            result += digits[digitIndex];
            digitIndex += 1;
          } else {
            break;
          }
        } else if (digitIndex < digits.length) {
          result += char;
        }
      }
      return result;
    },

    caretFromDigits(formatted, digitsCount, preferSeparator) {
      if (!formatted || digitsCount <= 0) {
        return 0;
      }
      let count = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          count += 1;
          if (count === digitsCount) {
            if (
              preferSeparator &&
              formatted[i + 1] &&
              /\D/.test(formatted[i + 1])
            ) {
              return i + 2;
            }
            return i + 1;
          }
        }
      }
      return formatted.length;
    },

    getInputEl() {
      if (!this.$refs.input || !this.$refs.input.$el) {
        return null;
      }
      return this.$refs.input.$el.querySelector("input");
    },

    setCaret(position) {
      this.$nextTick(() => {
        const input = this.getInputEl();
        if (input && typeof input.setSelectionRange === "function") {
          input.setSelectionRange(position, position);
        }
      });
    },
  },

  computed: {
    isMdy() {
      const format = String(this.formato || "").toUpperCase();
      if (format === "AUTO") {
        const locale = String(this.pickerLocale || "").toLowerCase();
        return locale.startsWith("en") || locale.startsWith("us");
      }
      return format === "MDY";
    },

    translatedLabel() {
      return translateIfMessageExists(this.$i18n, this.label);
    },

    hintText() {
      return this.isMdy ? "MM/DD/AAAA HH:MM" : "DD/MM/AAAA HH:MM";
    },

    placeholderText() {
      if (!this.isFocused && this.value) {
        return "";
      }
      return this.hintText;
    },

    appendIcon() {
      return this.showPicker ? "mdi-calendar-clock" : undefined;
    },

    pickerLocale() {
      return this.$i18n && this.$i18n.locale ? this.$i18n.locale : "pt-BR";
    },

    showSuccess() {
      return (
        !!this.value &&
        this.value.length === 16 &&
        this.isValidDateTime(this.value)
      );
    },

    showError() {
      return (
        !!this.value &&
        this.value.length === 16 &&
        !this.isValidDateTime(this.value)
      );
    },

    errorMessages() {
      if (!this.value || this.value.length !== 16) {
        return [];
      }
      return this.isValidDateTime(this.value) ? [] : this.$t("Data_invalida");
    },

    validationRules() {
      if (!this.obrigatorio) {
        return [];
      }
      return [(value) => !!value || this.$t("Campo_obrigatorio")];
    },

    formatedValue: function () {
      const data = this.value;
      if (!data) {
        return null;
      }
      if (data.length !== 16) {
        return null;
      }
      if (!this.isValidDateTime(data)) {
        return null;
      }
      const iso = this.displayToIso(data);
      return iso || data;
    },
  },
};
</script>

<style></style>
